import { useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { useSessionStore } from '@/store/session'
import { authApi } from '@/features/auth/api'
import { establishSession, privateQueryKey } from '@/features/auth/sessionCache'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PasswordField } from '@/components/ui/PasswordField'

/**
 * While the account's mail is not verified, every page says where the link went and offers it again: until then
 * the account cannot register companies or buy licences. A mistyped address is corrected here, with the password,
 * and the link goes to the corrected one. It reads the account again when the tab regains focus, so a link opened in
 * another tab clears it.
 */
export function EmailVerificationNotice() {
  const user = useSessionStore((state) => state.user)
  const location = useLocation()
  const queryClient = useQueryClient()
  const id = useId()
  const security = useQuery({
    queryKey: privateQueryKey(user?.id ?? 0, 'account-security'),
    queryFn: () => authApi.security(),
    enabled: Boolean(user),
    refetchOnWindowFocus: true,
  })
  const resend = useMutation({ mutationFn: () => authApi.requestEmailVerification() })
  const [sent, setSent] = useState('')
  const [changing, setChanging] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const change = useMutation({
    mutationFn: () => authApi.changeUnverifiedEmail(email.trim(), password),
    onSuccess: async (result) => {
      setChanging(false)
      setSent(result.message)
      // The session's token still names the mistaken address; a renewed one carries the corrected address.
      try {
        const session = await authApi.refresh()
        await establishSession(queryClient, session.user, session.accessToken)
      } catch {
        // The change stands; the token renews itself at the next refresh.
      }
      await security.refetch()
    },
  })

  if (!user || !security.data || security.data.emailVerified || location.pathname === '/verificar-correo') return null
  const emailError = submitted && !email.trim() ? 'Escribe el correo correcto.' : undefined
  const passwordError = submitted && !password ? 'Escribe tu contraseña.' : undefined
  const changeError = change.isError ? (change.error instanceof Error && change.error.message ? change.error.message : 'No pudimos cambiar el correo. Inténtalo de nuevo.') : undefined

  function openChange() {
    setEmail(''); setPassword(''); setSubmitted(false); change.reset(); setChanging(true)
  }
  function confirmChange() {
    setSubmitted(true)
    if (!email.trim() || !password) return
    setSent('')
    change.mutate()
  }

  return <section className="email-verification-callout email-verification-banner" aria-label="Verificación del correo">
    <p><strong>Verifica tu correo.</strong> Te enviamos un enlace a <strong>{security.data.email}</strong>. Ábrelo para registrar empresas y contratar licencias; si no lo encuentras, revisa la carpeta de correo no deseado.</p>
    {sent && <p role="status">{sent}</p>}
    {resend.isError && <p role="alert">{resend.error instanceof Error && resend.error.message ? resend.error.message : 'No pudimos enviar el correo de verificación. Inténtalo de nuevo.'}</p>}
    <div className="email-verification-actions">
      <Button type="button" emphasis="outline" busy={resend.isPending} onClick={() => { setSent(''); resend.mutate(undefined, { onSuccess: (result) => setSent(result.message) }) }}>Reenviar correo de verificación</Button>
      <Button type="button" emphasis="ghost" onClick={openChange}>Cambiar correo</Button>
    </div>
    <ConfirmDialog open={changing} title="Cambiar tu correo" action="Cambiar correo" intent="primary" busy={change.isPending} error={changeError}
      onCancel={() => { if (!change.isPending) setChanging(false) }} onConfirm={confirmChange}>
      <div className="email-change-fields">
        <p>El enlace que enviamos a <strong>{security.data.email}</strong> dejará de funcionar y te enviaremos uno nuevo al correo que escribas.</p>
        <div className="form-field">
          <label htmlFor={`${id}-email`}>Correo correcto</label>
          <input id={`${id}-email`} type="email" autoComplete="email" maxLength={255} value={email} onChange={(event) => setEmail(event.target.value)}
            aria-invalid={Boolean(emailError)} aria-describedby={emailError ? `${id}-email-error` : undefined} />
          {emailError && <p id={`${id}-email-error`} className="field-error" role="alert">{emailError}</p>}
        </div>
        <PasswordField label="Contraseña" autoComplete="current-password" value={password} onChange={setPassword} minLength={1}
          hint="La de tu cuenta del portal." error={passwordError} />
      </div>
    </ConfirmDialog>
  </section>
}
